/**
 * Shared database schema for Object-Property-LWW-value system
 * Used by both client (SQLite WASM) and server (Cloudflare Durable Objects)
 * 
 * Property-update-driven architecture:
 * - Properties table = Primary data store
 * - Objects table = Computed view (maintained by triggers from property updates)
 */

export const DATABASE_SCHEMA = {
  // Create objects table (materialized view maintained by triggers)
  CREATE_OBJECTS_TABLE: `
    CREATE TABLE IF NOT EXISTS objects (
      object_id TEXT PRIMARY KEY,
      parent_id TEXT,
      object_type TEXT NOT NULL,
      is_tombstoned BOOLEAN DEFAULT 0,
      created_at REAL NOT NULL,
      created_by_replica INTEGER NOT NULL,
      created_by_user TEXT NOT NULL,
      dirty INTEGER DEFAULT 0,
      content_hash TEXT,
      subtree_hash TEXT,
      updated_at REAL,
      version_vector TEXT -- JSON: {replica_id: max_logical_timestamp}
    )
  `,

  // Create properties table (event log)
  CREATE_PROPERTIES_TABLE: `
    CREATE TABLE IF NOT EXISTS object_properties (
      object_id TEXT NOT NULL,
      property TEXT NOT NULL,
      value TEXT,
      lww_counter INTEGER,  -- NULL for special properties (object.*)
      lww_random INTEGER,   -- NULL for special properties (object.*)
      replica_id INTEGER NOT NULL,
      logical_timestamp INTEGER NOT NULL,
      updated_at REAL NOT NULL,
      user_id TEXT NOT NULL,
      PRIMARY KEY (object_id, property)
    )
  `,

  // Trigger 1: Create object when object.metadata is inserted
  CREATE_OBJECT_FROM_METADATA_TRIGGER: `
    CREATE TRIGGER IF NOT EXISTS create_object_from_metadata
      AFTER INSERT ON object_properties
      WHEN NEW.property = 'object.metadata'
    BEGIN
      INSERT OR IGNORE INTO objects (
        object_id, 
        parent_id, 
        object_type, 
        is_tombstoned,
        created_at, 
        created_by_replica, 
        created_by_user,
        dirty,
        updated_at
      )
      SELECT 
        NEW.object_id,
        JSON_EXTRACT(NEW.value, '$.parent_id'),
        JSON_EXTRACT(NEW.value, '$.type'),
        CASE 
          WHEN EXISTS (
            SELECT 1 FROM tombstone_ancestry 
            WHERE object_id = COALESCE(JSON_EXTRACT(NEW.value, '$.parent_id'), '')
              AND has_tombstoned_ancestor = 1
          ) THEN 1
          ELSE 0
        END,
        NEW.updated_at,
        NEW.replica_id,
        NEW.user_id,
        1,
        NEW.updated_at;
        
      -- If ancestor is tombstoned, auto-create tombstone property
      INSERT OR IGNORE INTO object_properties (
        object_id, property, value, replica_id, logical_timestamp, updated_at, user_id
      )
      SELECT 
        NEW.object_id,
        'object.tombstoned',
        'true',
        NEW.replica_id,
        NEW.logical_timestamp,
        NEW.updated_at,
        NEW.user_id
      WHERE EXISTS (
        SELECT 1 FROM tombstone_ancestry 
        WHERE object_id = COALESCE(JSON_EXTRACT(NEW.value, '$.parent_id'), '')
          AND has_tombstoned_ancestor = 1
      );
    END
  `,

  // Trigger 2: Update object tombstone state when object.tombstoned is inserted
  UPDATE_OBJECT_TOMBSTONE_TRIGGER: `
    CREATE TRIGGER IF NOT EXISTS update_object_tombstone
      AFTER INSERT ON object_properties
      WHEN NEW.property = 'object.tombstoned' AND NEW.value = 'true'
    BEGIN
      UPDATE objects 
      SET is_tombstoned = 1 
      WHERE object_id = NEW.object_id;
    END
  `,
  
  // Trigger 3: Mark objects dirty when properties change (for hash and version vector recomputation)
  MARK_OBJECT_DIRTY_TRIGGER: `
    CREATE TRIGGER IF NOT EXISTS mark_object_dirty_on_property_change
      AFTER INSERT ON object_properties
    BEGIN
      -- Mark the object as dirty - hash and version vector recomputation handled together
      UPDATE objects
      SET dirty = 1, updated_at = (strftime('%s', 'now') * 1000)
      WHERE object_id = NEW.object_id;
    END
  `,

  // View: Document root hash data (hash computed in TypeScript)
  CREATE_DOCUMENT_ROOT_HASHES_VIEW: `
    CREATE VIEW IF NOT EXISTS document_root_hashes AS
    SELECT 
      'document' as document_type,
      GROUP_CONCAT(o.subtree_hash, '|' ORDER BY o.object_id) as root_hashes_data,
      MAX(o.updated_at) as computed_at,
      COUNT(o.object_id) as root_object_count
    FROM objects o
    WHERE o.parent_id IS NULL 
      AND o.subtree_hash IS NOT NULL
  `,

  // View: Check if object or ancestors are tombstoned (recursive CTE)
  CREATE_TOMBSTONE_ANCESTRY_VIEW: `
    CREATE VIEW IF NOT EXISTS tombstone_ancestry AS
    WITH RECURSIVE ancestry(object_id, current_object_id, is_tombstoned) AS (
      -- Base case: start with each object itself
      SELECT o.object_id, o.object_id, o.is_tombstoned
      FROM objects o
      UNION ALL
      -- Recursive case: traverse UP to parent objects
      SELECT a.object_id, parent.object_id, parent.is_tombstoned
      FROM ancestry a
      JOIN objects current ON current.object_id = a.current_object_id
      JOIN objects parent ON parent.object_id = current.parent_id
      WHERE current.parent_id IS NOT NULL
    )
    SELECT 
      object_id,
      MAX(is_tombstoned) as has_tombstoned_ancestor
    FROM ancestry
    GROUP BY object_id
  `
};

/**
 * Initialize database with property-update-driven schema
 * Works with both sql.js (client) and Cloudflare SQLite (server)
 */
export function initializePropertyDrivenDatabase(db: any): void {
  // Create tables
  db.exec(DATABASE_SCHEMA.CREATE_OBJECTS_TABLE);
  db.exec(DATABASE_SCHEMA.CREATE_PROPERTIES_TABLE);
  
  // Create triggers (event handlers)
  db.exec(DATABASE_SCHEMA.CREATE_OBJECT_FROM_METADATA_TRIGGER);
  db.exec(DATABASE_SCHEMA.UPDATE_OBJECT_TOMBSTONE_TRIGGER);
  db.exec(DATABASE_SCHEMA.MARK_OBJECT_DIRTY_TRIGGER);
  
  // Create views
  db.exec(DATABASE_SCHEMA.CREATE_DOCUMENT_ROOT_HASHES_VIEW);
  db.exec(DATABASE_SCHEMA.CREATE_TOMBSTONE_ANCESTRY_VIEW);
  
  console.log('Property-driven database schema initialized');
}

/**
 * Rebuild objects table from properties (consistency check)
 */
export function rebuildObjectsFromProperties(db: any): {objectsCreated: number, errors: string[]} {
  const errors: string[] = [];
  let objectsCreated = 0;
  
  // Clear existing objects table
  db.exec('DELETE FROM objects');
  
  // Get all object.metadata properties
  const cursor = db.exec(`
    SELECT object_id, value, replica_id, updated_at, user_id
    FROM object_properties 
    WHERE property = 'object.metadata'
    ORDER BY updated_at
  `);
  
  for (const row of cursor) {
    try {
      const metadata = JSON.parse(row.value);
      const parentId = metadata.parent_id || null;
      const objectType = metadata.type;
      
      if (!objectType) {
        errors.push(`Object ${row.object_id}: missing type in metadata`);
        continue;
      }
      
      // Check if this object has a tombstone property
      const tombstoneCheck = db.exec(`
        SELECT 1 FROM object_properties 
        WHERE object_id = ? AND property = 'object.tombstoned' AND value = 'true'
      `, [row.object_id]);
      
      const isTombstoned = tombstoneCheck.length > 0 ? 1 : 0;
      
      // Insert object
      db.exec(`
        INSERT OR REPLACE INTO objects (
          object_id, parent_id, object_type, is_tombstoned,
          created_at, created_by_replica, created_by_user
        ) VALUES (?, ?, ?, ?, ?, ?, ?)
      `, [
        row.object_id,
        parentId,
        objectType,
        isTombstoned,
        row.updated_at,
        row.replica_id,
        row.user_id
      ]);
      
      objectsCreated++;
      
    } catch (error) {
      errors.push(`Object ${row.object_id}: ${error}`);
    }
  }
  
  console.log(`Objects table rebuilt: ${objectsCreated} objects created, ${errors.length} errors`);
  return {objectsCreated, errors};
}
